import { z } from 'zod';
import { normalizarPlaca, reaisParaCentavos, TIPOS_ENTRADA, type TipoEntrada } from './estoque';

/** Campos aceitos no importador, com apelidos para o mapeamento automático. */
export const CAMPOS_IMPORTACAO = [
  { campo: 'placa', rotulo: 'Placa', obrigatorio: true, apelidos: ['placa', 'placa do veiculo'] },
  {
    campo: 'marca',
    rotulo: 'Marca',
    obrigatorio: true,
    apelidos: ['marca', 'fabricante', 'montadora'],
  },
  {
    campo: 'modelo',
    rotulo: 'Modelo',
    obrigatorio: true,
    apelidos: ['modelo', 'veiculo', 'carro'],
  },
  {
    campo: 'versao',
    rotulo: 'Versão',
    obrigatorio: false,
    apelidos: ['versao', 'versão', 'motorizacao'],
  },
  {
    campo: 'anoFabricacao',
    rotulo: 'Ano fabricação',
    obrigatorio: true,
    apelidos: ['ano fabricacao', 'ano fab', 'ano/fab', 'fabricacao', 'ano'],
  },
  {
    campo: 'anoModelo',
    rotulo: 'Ano modelo',
    obrigatorio: false,
    apelidos: ['ano modelo', 'ano mod', 'modelo ano'],
  },
  {
    campo: 'km',
    rotulo: 'KM',
    obrigatorio: true,
    apelidos: ['km', 'quilometragem', 'kilometragem', 'hodometro'],
  },
  { campo: 'cor', rotulo: 'Cor', obrigatorio: false, apelidos: ['cor'] },
  {
    campo: 'combustivel',
    rotulo: 'Combustível',
    obrigatorio: false,
    apelidos: ['combustivel', 'combustível'],
  },
  {
    campo: 'cambio',
    rotulo: 'Câmbio',
    obrigatorio: false,
    apelidos: ['cambio', 'câmbio', 'transmissao'],
  },
  { campo: 'chassi', rotulo: 'Chassi', obrigatorio: false, apelidos: ['chassi', 'vin'] },
  { campo: 'renavam', rotulo: 'RENAVAM', obrigatorio: false, apelidos: ['renavam'] },
  {
    campo: 'valorCompra',
    rotulo: 'Valor de compra',
    obrigatorio: true,
    apelidos: ['valor compra', 'valor de compra', 'custo', 'compra', 'preco de compra'],
  },
  {
    campo: 'preco',
    rotulo: 'Preço de venda',
    obrigatorio: false,
    apelidos: ['preco', 'preço', 'preco venda', 'preço de venda', 'valor venda', 'valor anunciado'],
  },
  {
    campo: 'tipoEntrada',
    rotulo: 'Tipo de entrada',
    obrigatorio: false,
    apelidos: ['tipo entrada', 'tipo de entrada', 'entrada', 'origem'],
  },
  {
    campo: 'dataEntrada',
    rotulo: 'Data de entrada',
    obrigatorio: false,
    apelidos: ['data entrada', 'data de entrada', 'entrada em', 'data compra'],
  },
  {
    campo: 'opcionais',
    rotulo: 'Opcionais',
    obrigatorio: false,
    apelidos: ['opcionais', 'itens', 'acessorios'],
  },
  {
    campo: 'observacoes',
    rotulo: 'Observações',
    obrigatorio: false,
    apelidos: ['observacoes', 'observações', 'obs'],
  },
  // Histórico de vendas: carro já vendido entra como vendido, com a venda (relatórios e DRE completos)
  {
    campo: 'valorVenda',
    rotulo: 'Valor vendido (carro já vendido)',
    obrigatorio: false,
    apelidos: ['valor vendido', 'vendido por', 'valor da venda', 'preco vendido', 'preço vendido'],
  },
  {
    campo: 'dataVenda',
    rotulo: 'Data da venda',
    obrigatorio: false,
    apelidos: ['data venda', 'data da venda', 'vendido em', 'data de venda'],
  },
  {
    campo: 'comprador',
    rotulo: 'Comprador',
    obrigatorio: false,
    apelidos: ['comprador', 'cliente', 'nome do cliente', 'nome comprador'],
  },
] as const;
export type CampoImportacao = (typeof CAMPOS_IMPORTACAO)[number]['campo'];
export const camposImportacao = CAMPOS_IMPORTACAO.map((c) => c.campo) as [
  CampoImportacao,
  ...CampoImportacao[],
];

/** coluna do arquivo → campo (ou null para ignorar) */
export const mapeamentoSchema = z
  .record(z.string(), z.enum(camposImportacao).nullable())
  .refine(
    (m) =>
      CAMPOS_IMPORTACAO.filter((c) => c.obrigatorio).every((c) =>
        Object.values(m).includes(c.campo),
      ),
    {
      message:
        'Mapeie todas as colunas obrigatórias: placa, marca, modelo, ano, km e valor de compra',
    },
  )
  .refine(
    (m) => {
      const usados = Object.values(m).filter(Boolean);
      return new Set(usados).size === usados.length;
    },
    { message: 'Cada campo só pode vir de uma coluna' },
  );

const semAcento = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[_\-.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export function sugerirMapeamento(cabecalhos: string[]): Record<string, CampoImportacao | null> {
  const r: Record<string, CampoImportacao | null> = {};
  const usados = new Set<string>();
  for (const cab of cabecalhos) {
    const n = semAcento(cab);
    const achado = CAMPOS_IMPORTACAO.find(
      (c) => !usados.has(c.campo) && c.apelidos.some((a) => semAcento(a) === n),
    );
    r[cab] = achado?.campo ?? null;
    if (achado) usados.add(achado.campo);
  }
  return r;
}

const ENTRADA_TEXTO: Record<string, TipoEntrada> = {
  compra: 'compra_pf',
  'compra pf': 'compra_pf',
  particular: 'compra_pf',
  'compra loja': 'compra_loja',
  loja: 'compra_loja',
  troca: 'troca',
  consignacao: 'consignacao',
  consignado: 'consignacao',
};

function inteiro(v: string): number | null {
  const s = v.replace(/[.\s]/g, '').replace(/km/i, '');
  return /^\d+$/.test(s) ? Number(s) : null;
}

function data(v: string): Date | null {
  const br = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(v.trim());
  if (br) return new Date(Number(br[3]), Number(br[2]) - 1, Number(br[1]));
  const iso = /^\d{4}-\d{2}-\d{2}/.test(v.trim()) ? new Date(v.trim()) : null;
  return iso && !Number.isNaN(iso.getTime()) ? iso : null;
}

export interface LinhaConvertida {
  placa: string;
  marca: string;
  modelo: string;
  versao: string | null;
  anoFabricacao: number;
  anoModelo: number;
  km: number;
  cor: string | null;
  combustivel: string | null;
  cambio: string | null;
  chassi: string | null;
  renavam: string | null;
  valorCompraCentavos: number;
  precoCentavos: number | null;
  tipoEntrada: TipoEntrada;
  entradaEm: string | null;
  opcionais: string[];
  observacoes: string | null;
  /** carro já vendido (histórico): valor, data e comprador */
  venda: { valorCentavos: number; data: string; comprador: string | null } | null;
}

/**
 * Converte uma linha bruta (texto) em veículo, devolvendo erros legíveis por linha.
 * Aceita formatos brasileiros: "45.900,00", "85.000 km", "2019/2020", "15/03/2026".
 */
export function converterLinha(
  bruta: Record<string, string>,
  mapa: Record<string, CampoImportacao | null>,
): { ok: true; valor: LinhaConvertida } | { ok: false; erros: string[] } {
  const v: Partial<Record<CampoImportacao, string>> = {};
  for (const [col, campo] of Object.entries(mapa))
    if (campo) v[campo] = (bruta[col] ?? '').toString().trim();
  const erros: string[] = [];
  const txt = (c: CampoImportacao) => (v[c] ? v[c]! : null);

  const placa = normalizarPlaca(v.placa ?? '');
  if (!/^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/.test(placa))
    erros.push(`Placa inválida: "${v.placa ?? ''}"`);
  if (!v.marca) erros.push('Marca vazia');
  if (!v.modelo) erros.push('Modelo vazio');

  let anoFab: number | null = null;
  let anoMod: number | null = null;
  const anoTxt = v.anoFabricacao ?? '';
  const par = /^(\d{4})\s*\/\s*(\d{2,4})$/.exec(anoTxt);
  if (par) {
    anoFab = Number(par[1]);
    anoMod = par[2]!.length === 2 ? Number(par[1]!.slice(0, 2) + par[2]) : Number(par[2]);
  } else anoFab = inteiro(anoTxt);
  if (v.anoModelo) anoMod = inteiro(v.anoModelo);
  anoMod ??= anoFab;
  const limite = new Date().getFullYear() + 1;
  if (!anoFab || anoFab < 1950 || anoFab > limite) erros.push(`Ano inválido: "${anoTxt}"`);
  else if (!anoMod || anoMod < anoFab || anoMod > anoFab + 1)
    erros.push('Ano modelo deve ser igual ou 1 a mais que o de fabricação');

  const km = inteiro(v.km ?? '');
  if (km === null) erros.push(`KM inválido: "${v.km ?? ''}"`);

  const compra = reaisParaCentavos(v.valorCompra ?? '');
  if (compra === null || compra <= 0)
    erros.push(`Valor de compra inválido: "${v.valorCompra ?? ''}"`);
  const preco = v.preco ? reaisParaCentavos(v.preco) : null;
  if (v.preco && (preco === null || preco <= 0)) erros.push(`Preço inválido: "${v.preco}"`);

  let tipoEntrada: TipoEntrada = 'compra_pf';
  if (v.tipoEntrada) {
    const t = semAcento(v.tipoEntrada).replace(/ /g, ' ');
    const achado = (TIPOS_ENTRADA as readonly string[]).includes(t)
      ? (t as TipoEntrada)
      : ENTRADA_TEXTO[t];
    if (!achado) erros.push(`Tipo de entrada desconhecido: "${v.tipoEntrada}"`);
    else tipoEntrada = achado;
  }
  let entradaEm: string | null = null;
  if (v.dataEntrada) {
    const d = data(v.dataEntrada);
    if (!d) erros.push(`Data de entrada inválida: "${v.dataEntrada}"`);
    else entradaEm = d.toISOString();
  }
  let venda: LinhaConvertida['venda'] = null;
  if (v.valorVenda || v.dataVenda) {
    const valor = v.valorVenda ? reaisParaCentavos(v.valorVenda) : null;
    const quando = v.dataVenda ? data(v.dataVenda) : null;
    if (!valor || valor <= 0) erros.push(`Valor vendido inválido: "${v.valorVenda ?? ''}"`);
    if (!quando) erros.push(`Data da venda inválida: "${v.dataVenda ?? ''}"`);
    else if (quando.getTime() > Date.now()) erros.push('Data da venda no futuro');
    if (valor && quando)
      venda = { valorCentavos: valor, data: quando.toISOString(), comprador: txt('comprador') };
  }
  const chassi = txt('chassi')?.toUpperCase() ?? null;
  if (chassi && !/^[A-HJ-NPR-Z0-9]{17}$/.test(chassi)) erros.push('Chassi deve ter 17 caracteres');
  const renavam = txt('renavam')?.replace(/\D/g, '') || null;
  if (renavam && !/^\d{9,11}$/.test(renavam)) erros.push('RENAVAM deve ter 9 a 11 números');

  if (erros.length) return { ok: false, erros };
  return {
    ok: true,
    valor: {
      placa,
      marca: v.marca!,
      modelo: v.modelo!,
      versao: txt('versao'),
      anoFabricacao: anoFab!,
      anoModelo: anoMod!,
      km: km!,
      cor: txt('cor'),
      combustivel: txt('combustivel'),
      cambio: txt('cambio'),
      chassi,
      renavam,
      valorCompraCentavos: compra!,
      precoCentavos: preco,
      tipoEntrada,
      entradaEm,
      opcionais: v.opcionais
        ? v.opcionais
            .split(/[;,|]/)
            .map((o) => o.trim())
            .filter(Boolean)
        : [],
      observacoes: txt('observacoes'),
      venda,
    },
  };
}

export interface ResumoImportacao {
  id: string;
  arquivoNome: string;
  status: 'analisando' | 'pronta' | 'processando' | 'concluida' | 'falhou' | 'desfeita';
  cabecalhos: string[];
  mapeamento: Record<string, CampoImportacao | null> | null;
  totalLinhas: number;
  validas: number;
  importadas: number;
  comErro: number;
  criadoEm: string;
  concluidaEm: string | null;
  desfeitaEm: string | null;
}
